import * as THREE from 'three';
import vertexShaderSource from './assets/shaders/vertex.glsl';
import fragmentShaderSource from './assets/shaders/fragment.glsl';
import GUI from 'lil-gui';

// Import assets so Vite includes them in the build
import texturePxUrl from './assets/texture/px.png?url';
import texturePyUrl from './assets/texture/py.png?url';
import texturePzUrl from './assets/texture/pz.png?url';
import audioUrl from './assets/battle-event-bgm.mp3?url';


// Scene setup
const ORTHO_SIZE = 5;
let scene, camera, renderer;
let shaderMaterial;
let mesh, cube1, cube2, cube3;
let time = 0;
let frameCount = 0;
let lastTime = performance.now();
let gui;

// Audio variables
let audioContext, analyser, dataArray;
let audioElement;
let lowFreq = 0, midFreq = 0, highFreq = 0;
let prevLowFreq = 0;
let playing = false;
let audioSource;

// Multi-band kick detection for better accuracy
let kickDetected = false;
let kickEnergy = 0;
let kickDecay = 0.8;
let kickThreshold = 0.05; // Lower threshold to catch more kicks
let kickSensitivity = 2.0;
let kickImpactDuration = 0;
let energyHistory = [];
const historyLength = 4; // Shorter history for faster response

// Create frequency band energy arrays for multi-band detection
let bandEnergies = Array(8).fill(0);
let bandHistories = Array(8).fill().map(() => []);

// Beat tracking variables
let beatTime = 0;
let lastKickTime = 0;
let beatInterval = 0;
let beatPhase = 0;

// Transition variables for smooth animation
let transitionFactor = 0; // 0 = not playing, 1 = playing
const transitionSpeed = 0.03; // Slower transition for more smoothness
let idleAnimation = 0; // Animation value when idle

let cube1_initRotation, cube2_initRotation, cube3_initRotation;



// Settings for dat.gui
const settings = {
    // Animation settings
    baseSpeed: 1.0,
    idleSpeed: 0.1,

    // Audio reactivity
    bassReactivity: 0.4, // More moderate
    midReactivity: 0.5,
    highReactivity: 0.4,

    // Kick settings - more subtle defaults
    kickReactivity: 0.6, // Reduced from 1.0
    bounceIntensity: 0.15, // More subtle bounce
    waveIntensity: 0.08,
    waveComplexity: 2.2,
    rippleIntensity: 0.25, // Reduced from 0.5

    // Visual settings
    lineThickness: 1.8,
    lineStraightness: 2.53,

    // Transition settings
    idleWaveHeight: 0.0624, // Small waves when idle
    transitionSmoothness: 0.03, // How smooth the transition is

    // Color settings
    bgColorDown: [10, 10, 20],
    bgColorUp: [5, 5, 15],
    color1In: [255, 0, 255], // Magenta
    color1Out: [128, 0, 255], // Purple
    color2In: [0, 255, 255], // Cyan
    color2Out: [0, 128, 255], // Blue
    color3In: [255, 255, 0], // Yellow
    color3Out: [255, 128, 0], // Orange

    
    // Grain settings
    enableGrain: false,
    grainIntensity: 0.075,
    grainSpeed: 2.0,
    grainMean: 0.0,
    grainVariance: 0.5,
    grainBlendMode: "Addition",
};



// Initialize Three.js scene
function init() {
    // Get DOM elements
    const container = document.getElementById("canvas-container");

    // Create scene
    scene = new THREE.Scene();

    // Create camera
    // camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    // camera.position.z = 1;
    const aspect = window.innerWidth / window.innerHeight;
    camera = new THREE.OrthographicCamera(-aspect * ORTHO_SIZE, aspect * ORTHO_SIZE, ORTHO_SIZE, -ORTHO_SIZE, 0.1, 100);
    camera.position.z = 10;

    // Create renderer
    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    // Create shader material
    shaderMaterial = new THREE.ShaderMaterial({
        vertexShader: vertexShaderSource,
        fragmentShader: fragmentShaderSource,
        uniforms: {
        iResolution: {
            value: new THREE.Vector2(window.innerWidth, window.innerHeight)
        },
        iTime: { value: 0 },
        iMouse: { value: new THREE.Vector2(0.5, 0.5) },
        lowFreq: { value: 0 },
        midFreq: { value: 0 },
        highFreq: { value: 0 },
        isPlaying: { value: false },
        transitionFactor: { value: 0 },
        lineStraightness: { value: settings.lineStraightness },
        idleAnimation: { value: 0 },
        idleWaveHeight: { value: settings.idleWaveHeight },

        // Enhanced kick/beat detection uniforms
        kickEnergy: { value: 0 },
        beatPhase: { value: 0 },
        bounceEffect: { value: 0 },

        // Settings uniforms
        baseSpeed: { value: settings.baseSpeed },
        idleSpeed: { value: settings.idleSpeed },
        bassReactivity: { value: settings.bassReactivity },
        midReactivity: { value: settings.midReactivity },
        highReactivity: { value: settings.highReactivity },
        kickReactivity: { value: settings.kickReactivity },
        bounceIntensity: { value: settings.bounceIntensity },
        waveIntensity: { value: settings.waveIntensity },
        waveComplexity: { value: settings.waveComplexity },
        rippleIntensity: { value: settings.rippleIntensity },
        lineThickness: { value: settings.lineThickness },

        // Grain uniforms
        enableGrain: { value: settings.enableGrain },
        grainIntensity: { value: settings.grainIntensity },
        grainSpeed: { value: settings.grainSpeed },
        grainMean: { value: settings.grainMean },
        grainVariance: { value: settings.grainVariance },
        grainBlendMode: { value: 0 }, // Default to Addition

        // Color uniforms
        bgColorDown: {
            value: new THREE.Vector3(
                settings.bgColorDown[0] / 255,
                settings.bgColorDown[1] / 255,
                settings.bgColorDown[2] / 255
            )
        },
        bgColorUp: {
            value: new THREE.Vector3(
                settings.bgColorUp[0] / 255,
                settings.bgColorUp[1] / 255,
                settings.bgColorUp[2] / 255
            )
        },
        color1In: {
            value: new THREE.Vector3(
                settings.color1In[0] / 255,
                settings.color1In[1] / 255,
                settings.color1In[2] / 255
            )
        },
        color1Out: {
            value: new THREE.Vector3(
                settings.color1Out[0] / 255,
                settings.color1Out[1] / 255,
                settings.color1Out[2] / 255
            )
        },
        color2In: {
            value: new THREE.Vector3(
                settings.color2In[0] / 255,
                settings.color2In[1] / 255,
                settings.color2In[2] / 255
            )
        },
        color2Out: {
            value: new THREE.Vector3(
                settings.color2Out[0] / 255,
                settings.color2Out[1] / 255,
                settings.color2Out[2] / 255
            )
        },
        color3In: {
            value: new THREE.Vector3(
                settings.color3In[0] / 255,
                settings.color3In[1] / 255,
                settings.color3In[2] / 255
            )
        },
        color3Out: {
            value: new THREE.Vector3(
                settings.color3Out[0] / 255,
                settings.color3Out[1] / 255,
                settings.color3Out[2] / 255
            )
        }
        }
    });

    // Create a plane geometry that fills the screen
    const geometry = new THREE.PlaneGeometry(aspect * ORTHO_SIZE * 2, ORTHO_SIZE * 2);

    // Create mesh with shader material
    mesh = new THREE.Mesh(geometry, shaderMaterial);
    mesh.position.z = -5;
    scene.add(mesh);

    // Load Texture
    const textureX =new THREE.MeshBasicMaterial({ map: new THREE.TextureLoader().load(texturePxUrl) });
    const textureY =new THREE.MeshBasicMaterial({ map: new THREE.TextureLoader().load(texturePyUrl) });
    const textureZ =new THREE.MeshBasicMaterial({ map: new THREE.TextureLoader().load(texturePzUrl) });
    

    textureX.wrapS = THREE.ClampToEdgeWrapping;
    textureX.wrapT = THREE.ClampToEdgeWrapping;
    textureX.magFilter = THREE.LinearFilter;
    textureX.minFilter = THREE.LinearFilter;

    textureY.wrapS = THREE.ClampToEdgeWrapping;
    textureY.wrapT = THREE.ClampToEdgeWrapping;
    textureY.magFilter = THREE.LinearFilter;
    textureY.minFilter = THREE.LinearFilter;

    textureZ.wrapS = THREE.ClampToEdgeWrapping;
    textureZ.wrapT = THREE.ClampToEdgeWrapping;
    textureZ.magFilter = THREE.LinearFilter;
    textureZ.minFilter = THREE.LinearFilter;

    const materials = [ textureX, textureX, textureY, textureY, textureZ, textureZ];

    const cubeGeometry_1 = new THREE.BoxGeometry(6, 3, 3);
    // Create first cube geometry
    cube1 = new THREE.Mesh(cubeGeometry_1, materials);
    scene.add(cube1);
    cube1.position.set(-7, -3.5, 0);

    cube1_initRotation =  Math.PI * -0.22;
    cube1.rotation.x = cube1_initRotation
    cube1.rotation.y = Math.PI * 0.16;
    cube1.rotation.z = Math.PI * 0.08;

    // Create second cube geometry
    const cubeGeometry_2 = new THREE.BoxGeometry(4, 2, 2);
    cube2 = new THREE.Mesh(cubeGeometry_2, materials);
    scene.add(cube2);
    cube2.position.set(7, -2, 0);

    cube2_initRotation =  Math.PI * -0.2;
    cube2.rotation.x = cube2_initRotation
    cube2.rotation.y = Math.PI * -0.16;
    // cube2.rotation.z = Math.PI * 0.09;

    // Create third cube geometry
    const cubeGeometry_3 = new THREE.BoxGeometry(3.6, 1.8, 1.8);
    cube3 = new THREE.Mesh(cubeGeometry_3, materials);
    scene.add(cube3);
    cube3.position.set(-3, 5, 0);

    cube3_initRotation =  Math.PI * 0.1;
    cube3.rotation.y = Math.PI * 0.2;
    cube3.rotation.x = cube3_initRotation
    // cube3.rotation.z = Math.PI * 0.1;


    // Set up dat.gui
    // setupGUI();

    // Set up event listeners
    setupEventListeners();

    // Set up audio
    setupAudio();

    // Start animation loop
    animate();
}

// Set up event listeners
function setupEventListeners() {
    // Handle window resize
    window.addEventListener("resize", () => {
        renderer.setSize(window.innerWidth, window.innerHeight);
        shaderMaterial.uniforms.iResolution.value.set(window.innerWidth,window.innerHeight);

        const aspect = window.innerWidth / window.innerHeight;
        camera.left = -aspect * ORTHO_SIZE;
        camera.right= aspect * ORTHO_SIZE;
        camera.updateProjectionMatrix();

        mesh.geometry.dispose();
        mesh.geometry = new THREE.PlaneGeometry(aspect * ORTHO_SIZE * 2, ORTHO_SIZE * 2);
    });

    // Handle play button
    document.getElementById("playButton").addEventListener("click", toggleAudio); 
}

// Set up audio
function setupAudio() {
    audioElement = new Audio();
    audioElement.crossOrigin = "anonymous";
    audioElement.preload = "auto";

    // Use the new audio URL
    audioElement.src = audioUrl;
    audioElement.loop = true;
}

// Toggle audio playback
function toggleAudio() {
    if (!playing) {
        // Initialize audio context if needed
        if (!audioContext) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        dataArray = new Uint8Array(analyser.frequencyBinCount);

        // Connect audio element to analyzer
        audioSource = audioContext.createMediaElementSource(audioElement);
        audioSource.connect(analyser);
        analyser.connect(audioContext.destination);
        }

        // Resume audio context (needed for newer browsers)
        audioContext.resume().then(() => {
        // Play the track
        audioElement.play().catch((e) => {
            console.error("Error playing audio:", e);
        });
        });

        document.getElementById("playButton").textContent = "MUSIC ON";
        playing = true;
        shaderMaterial.uniforms.isPlaying.value = true;

        // Reset beat tracking
        beatTime = 0;
        lastKickTime = 0;
        beatInterval = 0;
    } else {
        // Stop playback
        audioElement.pause();
        document.getElementById("playButton").textContent = "MUSIC OFF";
        playing = false;
        shaderMaterial.uniforms.isPlaying.value = false;
    }
}

// Improved multi-band frequency analysis with better kick detection
function updateFrequencies() {
    if (!playing || !analyser) return;

    analyser.getByteFrequencyData(dataArray);

    // Divide spectrum into bands for better analysis
    const bands = [
        { name: "sub", range: [1, 4] }, // Sub-bass (20-40Hz)
        { name: "kick", range: [4, 9] }, // Kick drum focus (~40-80Hz)
        { name: "bass", range: [9, 20] }, // Bass range (80-160Hz)
        { name: "lowMid", range: [20, 40] }, // Low-mids (160-320Hz)
        { name: "mid", range: [40, 80] }, // Mids (320-640Hz)
        { name: "highMid", range: [80, 160] }, // High-mids (640-1280Hz)
        { name: "high", range: [160, 300] }, // Highs (1280-2400Hz)
        { name: "veryHigh", range: [300, 500] } // Very high (2400Hz+)
    ];

    // Process each band
    for (let i = 0; i < bands.length; i++) {
        const [start, end] = bands[i].range;
        const bandSlice = dataArray.slice(start, end);
        const bandAvg = getWeightedAverage(bandSlice);

        // Store current band energy (0-1 range)
        bandEnergies[i] = bandAvg;

        // Keep history for each band
        if (!bandHistories[i]) bandHistories[i] = [];
        bandHistories[i].unshift(bandAvg);
        if (bandHistories[i].length > historyLength) {
        bandHistories[i].pop();
        }
    }

    // Focus on kick drum band (band index 1) and bass band (band index 2)
    const kickAvg = bandEnergies[1];
    const bassAvg = bandEnergies[2];

    // Calculate recent history average for kick band
    const kickHistory = bandHistories[1];
    const recentKickAvg =
        kickHistory.slice(1).reduce((sum, val) => sum + val, 0) /
        (kickHistory.length - 1 || 1);

    // Enhanced kick detection - detect sudden spikes in energy
    // 1. Current energy must be significantly higher than recent average
    // 2. Absolute energy must be above a minimum threshold
    // 3. Must have been some time since last kick to avoid false positives
    const kickJump = kickAvg - recentKickAvg;
    const newKickDetected =
        kickJump > kickThreshold * 1.2 &&
        kickAvg > 0.15 &&
        (!kickDetected || performance.now() - lastKickTime > 150);

    // Handle kick detection
    if (newKickDetected) {
        // New kick detected
        kickDetected = true;
        kickEnergy = Math.min(1.0, kickAvg * kickSensitivity); // Scale with sensitivity
        kickImpactDuration = 10; // Set impact duration (frames)

        // Track beat timing
        const now = performance.now();
        if (lastKickTime > 0) {
        // Calculate beat interval from previous kick
        const newInterval = now - lastKickTime;
        if (newInterval > 200 && newInterval < 2000) {
            // Reasonable beat range
            beatInterval = beatInterval * 0.7 + newInterval * 0.3; // Smooth the intervals
        }
        }
        lastKickTime = now;
        beatTime = 0; // Reset beat phase

        console.log(
        "KICK detected!",
        kickAvg.toFixed(2),
        "jump:",
        kickJump.toFixed(2)
        );

    } else {
        // Decay kick energy
        kickEnergy *= kickDecay;

        // Reset detection when energy drops below threshold
        if (kickEnergy < 0.05) {
        kickDetected = false;
        }

        // Count down impact duration
        if (kickImpactDuration > 0) {
        kickImpactDuration--;
        }
    }

    // Calculate beat phase (0.0 to 1.0 representing position in beat cycle)
    if (beatInterval > 0) {
        beatTime += 16.67; // Approximately 60fps (16.67ms per frame)
        beatPhase = (beatTime % beatInterval) / beatInterval;
    }

    // Calculate bounce effect - more subtle
    let bounceValue = 0;
    if (kickImpactDuration > 0) {
        // Initial impact when kick is detected - more subtle curve
        bounceValue = Math.pow(kickImpactDuration / 10, 0.6) * 0.03;
    }

    // Add a smaller boost from kick energy
    bounceValue += kickEnergy * 0.025;

    // Apply frequency smoothing with different attack/decay rates

    // Bass frequencies (average of kick and bass bands)
    const combinedBass = (bandEnergies[1] * 1.2 + bandEnergies[2]) / 2.2;

    // Fast attack, medium decay for bass
    if (combinedBass > lowFreq * 1.1) {
        lowFreq = lowFreq * 0.3 + combinedBass * 0.7; // Fast attack
    } else {
        lowFreq = lowFreq * 0.85 + combinedBass * 0.15; // Medium decay
    }

    // Mid frequencies (average of lowMid and mid bands)
    const combinedMid = (bandEnergies[3] + bandEnergies[4]) / 2;

    // Medium attack/decay for mids
    if (combinedMid > midFreq * 1.1) {
        midFreq = midFreq * 0.4 + combinedMid * 0.6;
    } else {
        midFreq = midFreq * 0.8 + combinedMid * 0.2;
    }

    // High frequencies (average of highMid, high, and veryHigh bands)
    const combinedHigh =
        (bandEnergies[5] + bandEnergies[6] + bandEnergies[7]) / 3;

    // Balanced attack/decay for highs
    if (combinedHigh > highFreq * 1.05) {
        highFreq = highFreq * 0.5 + combinedHigh * 0.5;
    } else {
        highFreq = highFreq * 0.8 + combinedHigh * 0.2;
    }

    // Boost low frequencies with kick energy (more subtle)
    lowFreq = Math.max(lowFreq, kickEnergy * 0.6);

    // Update shader uniforms
    shaderMaterial.uniforms.lowFreq.value = lowFreq;
    shaderMaterial.uniforms.midFreq.value = midFreq;
    shaderMaterial.uniforms.highFreq.value = highFreq;
    shaderMaterial.uniforms.kickEnergy.value = kickEnergy;
    shaderMaterial.uniforms.beatPhase.value = beatPhase;
    shaderMaterial.uniforms.bounceEffect.value = bounceValue;

    // Update debug display if enabled
    if (settings.showDebug) {
        updateDebugDisplay();
    }
}

// Improved average calculation with emphasis on peaks
function getWeightedAverage(array) {
    if (array.length === 0) return 0;

    let sum = 0;
    let weight = 0;
    let maxVal = 0;
    const emphasizeFactor = 1.5; // Reduced from 1.8 for more linear response

    for (let i = 0; i < array.length; i++) {
        // Normalize value 0-1
        const value = array[i] / 255;
        maxVal = Math.max(maxVal, value);

        // Apply non-linear emphasis to higher values
        const emphasized = Math.pow(value, emphasizeFactor);

        sum += emphasized;
        weight++;
    }

    // Combine average with max value for better peak detection
    const avg = sum / weight;
    return avg * 0.7 + maxVal * 0.3; // Blend average and peak
}





function easeInOutExpo(x) {
    return x === 0 ? 0 : x === 1 ? 1 : x < 0.5 ? Math.pow(2, 20 * x - 10) / 2 : (2 - Math.pow(2, -20 * x + 10)) / 2;
}

const CUBE1_TOTAL_DURATION = 300; 
let cube1_currentFrame = 0;    

const CUBE2_TOTAL_DURATION = 200; 
let cube2_currentFrame = 0; 

const CUBE3_TOTAL_DURATION = 250; 
let cube3_currentFrame = 0; 

const TOTAL_ROTATION = Math.PI * 2; 

// Animation loop
function animate(timestamp) {
    requestAnimationFrame(animate);

    // Update time uniform
    time += 0.01;
    shaderMaterial.uniforms.iTime.value = time;

    // Update idle animation even when not playing
    idleAnimation += 0.01;
    shaderMaterial.uniforms.idleAnimation.value = idleAnimation;


    // Update transition factor for smooth animation - use transitionSmoothness
    const transitionRate = settings.transitionSmoothness;

    if (playing && transitionFactor < 1.0) {
        // Gradually increase transition factor when starting music
        transitionFactor = Math.min(transitionFactor + transitionRate, 1.0);
        shaderMaterial.uniforms.transitionFactor.value = transitionFactor;

    } else if (!playing && transitionFactor > 0.0) {
        // Gradually decrease transition factor when stopping music
        transitionFactor = Math.max(transitionFactor - transitionRate, 0.0);
        shaderMaterial.uniforms.transitionFactor.value = transitionFactor;

        // Only reset frequency values when transition is complete
        if (transitionFactor === 0) {
        lowFreq = 0;
        midFreq = 0;
        highFreq = 0;
        kickEnergy = 0;
        shaderMaterial.uniforms.lowFreq.value = 0;
        shaderMaterial.uniforms.midFreq.value = 0;
        shaderMaterial.uniforms.highFreq.value = 0;
        shaderMaterial.uniforms.kickEnergy.value = 0;
        shaderMaterial.uniforms.bounceEffect.value = 0;
        }
    }

    if (playing) {

        let cube1_linearProgress = cube1_currentFrame / CUBE1_TOTAL_DURATION;
        let cube2_linearProgress = cube2_currentFrame / CUBE2_TOTAL_DURATION;
        let cube3_linearProgress = cube3_currentFrame / CUBE3_TOTAL_DURATION;
        

        if (cube1_linearProgress > 1) {
            cube1_currentFrame = 0;
            cube1_linearProgress = 0; 
        }

        if (cube2_linearProgress > 1) {
            cube2_currentFrame = 0;
            cube2_linearProgress = 0; 
        }

        if (cube3_linearProgress > 1) {
            cube3_currentFrame = 0;
            cube3_linearProgress = 0; 
        }
    
        const cube1_easedProgress = easeInOutExpo(cube1_linearProgress);
        const cube2_easedProgress = easeInOutExpo(cube2_linearProgress);
        const cube3_easedProgress = easeInOutExpo(cube3_linearProgress);
    
        cube1.rotation.x = cube1_initRotation + (TOTAL_ROTATION * cube1_easedProgress);
        cube2.rotation.x = cube2_initRotation + (TOTAL_ROTATION * cube2_easedProgress);
        cube3.rotation.x = cube3_initRotation + (TOTAL_ROTATION * cube3_easedProgress);

        cube1_currentFrame++;
        cube2_currentFrame++;
        cube3_currentFrame++;
    }


    // Update audio frequencies
    updateFrequencies();

    // Render the scene
    renderer.render(scene, camera);

    
}

// Initialize when the page loads
window.onload = () => {
    init();
};
